import React, { ComponentProps, ReactNode } from 'react';
import { useAppContext } from '../../contexts/AppContext/AppContext';
import { asFetchableBlock, useRefreshBlock } from '../../_lib/BlockFetcher';
import { BlockType } from '../../types/api/ElementType';
import { CheckBoxParam, Form, Row, SonForm, SubmitButton, Switch, useForm } from '../../_lib/Form2';
import { useSonTranslation } from '../../translation/useSonTranslation';
import { translatePartOfStringWithLink } from '../../lib/StringUtils/StringHTMLUtils';
import { Namespace } from '../../translation/namespace';
import { ContentParagraph, ContentParagraphModifier } from '../../_lib/components/ContentParagraph';

type AccessKeysSettingsProps = {
  accessKeysSettingsForm: SonForm<CheckBoxParam<'accessKeysEnabled'>>;
  helpPageUrl: string;
};

function AccessKeysSettings({ accessKeysSettingsForm, helpPageUrl }: AccessKeysSettingsProps) {
  const { t } = useSonTranslation('AccessKeysSettingsForm');
  const { enableAccessKeys, disableAccessKeys } = useAppContext();
  const { delayedRefresh } = useRefreshBlock();

  const { formProps, params, fetchState } = useForm({
    form: accessKeysSettingsForm,
    onSuccess: () => {
      const checkBoxRef = formProps.ref.current?.elements.namedItem('accessKeysEnabled') as HTMLInputElement;
      if (checkBoxRef.checked) {
        enableAccessKeys();
      } else {
        disableAccessKeys();
      }

      delayedRefresh();
    },
  });

  const accessKeyEnabledParam = params.getByName('accessKeysEnabled');
  const getForwardText = (): ReactNode => {
    return translatePartOfStringWithLink(`${Namespace.AccessKeysSettingsForm}:helpText`, helpPageUrl, false);
  };

  return (
    <Form id="access-keys-settings-form" {...formProps}>
      <ContentParagraph modifier={ContentParagraphModifier.MARGIN_BOTTOM}>{getForwardText()}</ContentParagraph>
      <Row>
        <Switch label={t('accessKeysEnabled')} param={accessKeyEnabledParam} modifierLabel="position-inherit" />
      </Row>
      <SubmitButton
        buttonLabel={t('saveSettings')}
        messageSuccess={t('phrases:changeSuccesfull')}
        messageSending={t('phrases:processIsCurrentlyRunningWithDots')}
        messageFail={t('phrases:changesCouldNotBeSaved')}
        fetchState={fetchState}
      />
    </Form>
  );
}

export const AccessKeysSettingsBlock = asFetchableBlock(AccessKeysSettings, BlockType.accesskeyssettings);
export type AccessKeysSettingsBlock = ComponentProps<typeof AccessKeysSettingsBlock>;
